// ============================================================================
// MÓDULO 11: TIMESHEET MANAGER (VERSÃO 3.0 - BILLING INTEGRATED)
// ARQUIVO: src/11_Timesheet_Manager.jsx
// OBJETIVO: Registrar horas e FATURAR diretamente para o Financeiro.
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { FIREBASE_CONFIG } from '../../config/Config';
import { SaveCancelBar } from '../../components/ui/SaveCancelBar';
import { logClientPortalEvent } from '../../utils/portalEventLog';
import { loadTimesheetEntries, addTimesheetEntry, deleteTimesheetEntry, createBillingBatch } from '../../services/TimesheetManagerRepository';
import { toTitleCasePt } from '../../utils/formatters';

const appId = FIREBASE_CONFIG.projectId;

// --- UTILITÁRIOS ---
const formatTime = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
    const s = (totalSeconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
};

const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

const exportTimesheetToCSV = (entries) => {
    if (!entries.length) return alert("Sem dados para exportar.");
    const headers = ["Data", "Cliente/Processo", "Tarefa", "Descrição", "Duração", "Segundos", "Cobrável", "Faturado", "Origem"];
    const rows = entries.map(e => {
        const dateObj = e.createdAt?.seconds ? new Date(e.createdAt.seconds * 1000) : new Date();
        return [
            dateObj.toLocaleDateString('pt-BR'),
            `"${toTitleCasePt(e.clientName) || ''}"`,
            e.taskType || 'Geral',
            `"${e.description || ''}"`,
            formatTime(e.seconds || 0),
            e.seconds || 0,
            e.billable ? 'SIM' : 'NÃO',
            e.billed ? 'SIM' : 'NÃO',
            e.source || 'MANUAL'
        ];
    });
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `timesheet_relatorio_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
};

const TASK_TYPES = ["Pesquisa Jurídica", "Redação de Peça", "Reunião com Cliente", "Audiência", "Diligência Externa", "Análise de Processo", "Administrativo"];

// --- CRONÔMETRO RESILIENTE (MANTIDO) ---
const ResilientTimer = ({ onSaveSession, autoStartData }) => {
    const [displayTime, setDisplayTime] = useState(0);
    const [isRunning, setIsRunning] = useState(false);
    const intervalRef = useRef(null);

    useEffect(() => {
        const integrationBuffer = localStorage.getItem('rda_timesheet_buffer');
        if (integrationBuffer && !localStorage.getItem('rda_timer_state')) {
            const payload = JSON.parse(integrationBuffer);
            if (payload.autoStart) {
                localStorage.removeItem('rda_timesheet_buffer');
                if (autoStartData) autoStartData(payload);
                handleStartInternal();
            }
        } else {
            const savedState = localStorage.getItem('rda_timer_state');
            if (savedState) {
                const { startTimestamp, accumulated, running } = JSON.parse(savedState);
                if (running) {
                    const now = Date.now();
                    const deltaSinceStart = Math.floor((now - startTimestamp) / 1000);
                    setDisplayTime(accumulated + deltaSinceStart);
                    setIsRunning(true);
                } else {
                    setDisplayTime(accumulated);
                }
            }
        }
    }, []);

    useEffect(() => {
        if (isRunning) {
            intervalRef.current = setInterval(() => {
                const now = Date.now();
                const savedState = JSON.parse(localStorage.getItem('rda_timer_state') || '{}');
                const start = savedState.startTimestamp || now;
                const baseAccumulated = savedState.accumulated || 0;
                setDisplayTime(Math.floor((now - start) / 1000) + baseAccumulated);
            }, 1000);
        } else { clearInterval(intervalRef.current); }
        return () => clearInterval(intervalRef.current);
    }, [isRunning]);

    const handleStartInternal = () => {
        const now = Date.now();
        localStorage.setItem('rda_timer_state', JSON.stringify({ startTimestamp: now, accumulated: displayTime, running: true }));
        setIsRunning(true);
    };

    const handleStart = () => { if (isRunning) return; handleStartInternal(); };
    
    const handlePause = () => {
        if (!isRunning) return;
        const savedState = JSON.parse(localStorage.getItem('rda_timer_state'));
        const now = Date.now();
        const delta = Math.floor((now - savedState.startTimestamp) / 1000);
        const newAccumulated = (savedState.accumulated || 0) + delta;
        localStorage.setItem('rda_timer_state', JSON.stringify({ startTimestamp: null, accumulated: newAccumulated, running: false }));
        setDisplayTime(newAccumulated);
        setIsRunning(false);
    };

    const handleStopAndSave = () => {
        handlePause();
        setTimeout(() => {
            onSaveSession(displayTime);
            setDisplayTime(0);
            setIsRunning(false);
            localStorage.removeItem('rda_timer_state');
        }, 100);
    };

    const handleDiscard = () => {
        if (confirm("Descartar tempo?")) {
            setDisplayTime(0);
            setIsRunning(false);
            localStorage.removeItem('rda_timer_state');
            localStorage.removeItem('rda_session_meta');
        }
    };

    const isLongSession = displayTime > 14400;

    return (
        <div className={`text-white p-6 rounded-xl shadow-lg mb-8 border border-gray-700 relative overflow-hidden transition-colors ${isLongSession ? 'bg-red-900' : 'bg-[#1a1e30]'}`}>
            {isRunning && <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 to-blue-500 animate-pulse"></div>}
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                <div className="flex items-center gap-4">
                    <div className={`w-4 h-4 rounded-full shadow-lg transition-all duration-500 ${isRunning ? 'bg-green-500 shadow-green-500/50 animate-ping' : 'bg-red-500'}`}></div>
                    <div>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">TEMPO DECORRIDO</p>
                        <span className="font-mono text-4xl font-bold tracking-wider text-white shadow-black drop-shadow-md">{formatTime(displayTime)}</span>
                        {isLongSession && <p className="text-[10px] text-yellow-300 font-bold mt-1 animate-pulse"><i className="fas fa-exclamation-triangle"></i> VERIFIQUE SE NÃO ESQUECEU LIGADO</p>}
                    </div>
                </div>
                <div className="flex gap-3">
                    {!isRunning ? (
                        <button onClick={handleStart} className="px-6 py-3 bg-green-600 hover:bg-green-500 text-white rounded-lg font-bold text-xs flex items-center gap-2 transition-all shadow-lg active:scale-95"><i className="fas fa-play"></i> INICIAR</button>
                    ) : (
                        <button onClick={handlePause} className="px-6 py-3 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg font-bold text-xs flex items-center gap-2 transition-all shadow-lg active:scale-95"><i className="fas fa-pause"></i> PAUSAR</button>
                    )}
                    <button onClick={handleStopAndSave} disabled={displayTime < 10} className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold text-xs flex items-center gap-2 transition-all shadow-lg disabled:opacity-50"><i className="fas fa-save"></i> SALVAR</button>
                    <button onClick={handleDiscard} className="px-4 py-3 bg-gray-700 hover:bg-red-900/50 text-gray-300 hover:text-white rounded-lg font-bold text-xs transition-all"><i className="fas fa-trash"></i></button>
                </div>
            </div>
        </div>
    );
};

// --- MODAL DE FATURAMENTO (BILLING MODAL - NEW V3.0) ---
const BillingModal = ({ selectedEntries, onClose, onConfirm }) => {
    const [hourlyRate, setHourlyRate] = useState(500); // Valor padrão
    const totalSeconds = selectedEntries.reduce((acc, e) => acc + (e.seconds || 0), 0);
    const totalHours = totalSeconds / 3600;
    const totalValue = totalHours * hourlyRate;
    const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);

    const handleConfirm = () => {
        onConfirm({
            totalValue: parseFloat(totalValue.toFixed(2)),
            dueDate,
            description: `Faturamento de Horas (${selectedEntries.length} itens) - ${selectedEntries[0]?.clientName || 'Diversos'}`
        });
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in-up">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden border-t-4 border-green-600">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-green-50">
                    <h3 className="font-bold text-green-900 text-sm flex items-center gap-2"><i className="fas fa-file-invoice-dollar"></i> Faturar Horas</h3>
                    <button onClick={onClose}><i className="fas fa-times text-gray-400 hover:text-red-500"></i></button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="bg-blue-50 p-3 rounded text-xs text-blue-800 border border-blue-100">
                        Você selecionou <strong>{selectedEntries.length}</strong> registros.
                        <br/>Tempo Total: <strong>{formatTime(totalSeconds)}</strong>
                    </div>
                    
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Valor Hora Técnica (R$)</label>
                        <input type="number" className="w-full p-2 border rounded font-bold text-gray-800" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} />
                    </div>

                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Total a Faturar</label>
                        <div className="text-2xl font-extrabold text-green-600">{formatCurrency(totalValue)}</div>
                    </div>

                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Vencimento</label>
                        <input type="date" className="w-full p-2 border rounded text-sm" value={dueDate} onChange={e => setDueDate(e.target.value)} />
                    </div>
                </div>
                <div className="p-4 bg-gray-50 border-t border-gray-100">
                    <SaveCancelBar
                        onCancel={onClose}
                        onSave={handleConfirm}
                        cancelLabel="Cancelar"
                        saveLabel="Confirmar"
                    />
                </div>
            </div>
        </div>
    );
};

// --- COMPONENTE: LISTA COM SELEÇÃO ---
const TimesheetList = ({ entries, onDelete, selectedIds, onToggleSelect, onSelectAll }) => {
    const allSelected = entries.length > 0 && selectedIds.length === entries.length;

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-300 overflow-hidden">
            <div className="p-4 bg-gray-50 border-b border-gray-300 flex justify-between items-center">
                <span className="font-bold text-xs text-gray-500 uppercase">Histórico de Atividades</span>
                <span className="bg-blue-100 text-blue-800 text-[10px] font-bold px-2 py-1 rounded border border-blue-200">HOURLY RATE</span>
            </div>
            <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th className="p-4 w-10 text-center">
                            <input type="checkbox" checked={allSelected} onChange={(e) => onSelectAll(e.target.checked)} className="rounded text-brand-blue cursor-pointer"/>
                        </th>
                        <th className="p-4 text-xs font-bold text-gray-400 uppercase">Data</th>
                        <th className="p-4 text-xs font-bold text-gray-400 uppercase">Cliente / Processo</th>
                        <th className="p-4 text-xs font-bold text-gray-400 uppercase">Tarefa</th>
                        <th className="p-4 text-xs font-bold text-gray-400 uppercase text-center">Status</th>
                        <th className="p-4 text-xs font-bold text-gray-400 uppercase text-right">Duração</th>
                        <th className="p-4 w-10"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {entries.length === 0 ? (
                        <tr><td colSpan="7" className="p-8 text-center text-gray-400 text-xs italic">Nenhum registro encontrado.</td></tr>
                    ) : (
                        entries.map((entry) => (
                            <tr key={entry.id} className={`hover:bg-gray-50 transition-colors ${entry.billed ? 'bg-green-50/30' : ''}`}>
                                <td className="p-4 text-center">
                                    {!entry.billed && entry.billable && (
                                        <input 
                                            type="checkbox" 
                                            checked={selectedIds.includes(entry.id)} 
                                            onChange={() => onToggleSelect(entry.id)}
                                            className="rounded text-brand-blue cursor-pointer"
                                        />
                                    )}
                                </td>
                                <td className="p-4 text-xs font-mono text-gray-600">{new Date(entry.createdAt?.seconds * 1000).toLocaleDateString('pt-BR')}</td>
                                <td className="p-4 text-sm font-bold text-gray-800">{toTitleCasePt(entry.clientName)}</td>
                                <td className="p-4 text-xs text-gray-600 max-w-md truncate">
                                    <span className="font-bold text-gray-700 block">{entry.taskType}</span>
                                    {entry.description}
                                </td>
                                <td className="p-4 text-center">
                                    {entry.billed ? <span className="text-[9px] bg-green-100 text-green-700 px-1.5 rounded border border-green-200 font-bold">FATURADO</span> : 
                                     (entry.billable ? <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 rounded font-bold">ABERTO</span> : <span className="text-[9px] text-gray-400">INTERNO</span>)}
                                </td>
                                <td className="p-4 text-sm font-mono font-bold text-brand-blue text-right">{formatTime(entry.seconds)}</td>
                                <td className="p-4 text-right">
                                    <button onClick={() => onDelete(entry.id)} className="text-gray-300 hover:text-red-500 opacity-50 hover:opacity-100"><i className="fas fa-trash"></i></button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

// --- MAIN CONTAINER ---
export const TimesheetManager = ({ exportTrigger = 0, extClientName = null, onClientNameChange = null, extTaskType = null, onTaskTypeChange = null, extIsBillable = null, onIsBillableChange = null }) => {
    const [entries, setEntries] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showBillingModal, setShowBillingModal] = useState(false);
    const [tempSeconds, setTempSeconds] = useState(0);
    const [selectedIds, setSelectedIds] = useState([]);
    
    const [form, setForm] = useState({ 
        clientName: '', 
        taskType: 'Pesquisa Jurídica', 
        description: '', 
        source: 'MANUAL',
        isBillable: true 
    });

    const clientName = extClientName !== null ? extClientName : form.clientName;
    const taskType = extTaskType !== null ? extTaskType : form.taskType;
    const isBillable = extIsBillable !== null ? extIsBillable : form.isBillable;
    const setClientName = (v) => { setForm(p => ({...p, clientName: v})); if (onClientNameChange) onClientNameChange(v); };
    const setTaskType = (v) => { setForm(p => ({...p, taskType: v})); if (onTaskTypeChange) onTaskTypeChange(v); };
    const setIsBillable = (v) => { setForm(p => ({...p, isBillable: v})); if (onIsBillableChange) onIsBillableChange(v); };


    useEffect(() => { loadEntries(); loadDraft(); }, []);
    useEffect(() => { if (exportTrigger > 0) exportTimesheetToCSV(entries); }, [exportTrigger]);
    useEffect(() => { if (clientName || form.description) localStorage.setItem('rda_session_meta', JSON.stringify({...form, clientName, taskType, isBillable})); }, [form]);

    const loadDraft = () => {
        const draft = localStorage.getItem('rda_session_meta');
        if (draft) {
            const parsed = JSON.parse(draft);
            setForm(parsed);
            if (onClientNameChange) onClientNameChange(parsed.clientName || '');
            if (onTaskTypeChange) onTaskTypeChange(parsed.taskType || 'Pesquisa Jurídica');
            if (onIsBillableChange) onIsBillableChange(parsed.isBillable !== false);
        }
    };

    const loadEntries = async () => {
        try {
            const rows = await loadTimesheetEntries(appId);
            setEntries(rows);
        } catch (e) { console.error("Erro load timesheet:", e); }
    };

    const handleAutoStartData = (data) => {
        setClientName(data.clientName);
        setTaskType('Audiência');
        setIsBillable(true);
        setForm(p => ({...p, description: data.description, source: 'AGENDA'}));
    };

    const handlePreSave = (seconds) => { setTempSeconds(seconds); setShowModal(true); };

    const handleConfirmSave = async () => {
        if (!clientName || !form.description) return alert("Preencha o cliente e a descrição.");
        try {
            await addTimesheetEntry({ clientName, taskType, description: form.description, source: form.source, seconds: tempSeconds, billable: isBillable, billed: false }, appId);
            setShowModal(false);
            setClientName('');
            setTaskType('Pesquisa Jurídica');
            setIsBillable(true);
            setForm({ clientName: '', taskType: 'Pesquisa Jurídica', description: '', source: 'MANUAL', isBillable: true });
            localStorage.removeItem('rda_session_meta');
            loadEntries();
        } catch (e) { alert("Erro ao salvar: " + e.message); }
    };

    const handleDelete = async (id) => {
        if(!confirm("Excluir este registro?")) return;
        try { await deleteTimesheetEntry(id, appId); loadEntries(); } catch(e) { alert("Erro: " + e.message); }
    };

    // --- LÓGICA DE SELEÇÃO E FATURAMENTO (V3.0) ---
    const handleToggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            // Seleciona apenas os cobráveis e não faturados
            const selectable = entries.filter(e => e.billable && !e.billed).map(e => e.id);
            setSelectedIds(selectable);
        } else {
            setSelectedIds([]);
        }
    };

    const handleBillingConfirm = async (billingData) => {
        try {
            const firstEntry = entries.find(e => selectedIds.includes(e.id)) || {};
            const targetClientId = String(firstEntry?.clientId || '').trim();
            const { ledgerId } = await createBillingBatch(billingData, selectedIds, entries, appId);
            try {
                if (targetClientId) {
                    await logClientPortalEvent({
                        clientId: targetClientId,
                        type: 'FINANCIAL_ENTRY_POSTED',
                        actorName: 'Escritório',
                        category: 'SYSTEM',
                        payload: {
                            ledgerId: String(newRecRef.id || '').trim() || null,
                            group: 'RECEITA_BRUTA',
                            category: 'Honorários por Hora',
                            value: Number(billingData?.totalValue || 0),
                            text: `Recebível lançado a partir do timesheet: ${String(billingData?.description || 'Sem descrição').trim() || 'Sem descrição'}`,
                        },
                    });
                }
            } catch (e) {
                void e;
            }
            setShowBillingModal(false);
            setSelectedIds([]);
            loadEntries();
            alert("✅ Faturamento gerado com sucesso! Verifique o Módulo Financeiro.");

        } catch (e) {
            console.error(e);
            alert("Erro ao faturar: " + e.message);
        }
    };

    return (
        <div>
            {selectedIds.length > 0 && (
                <div className="flex justify-end mb-4">
                    <button onClick={() => setShowBillingModal(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg text-xs font-bold shadow hover:bg-green-700 flex items-center gap-2">
                        <i className="fas fa-file-invoice-dollar"></i> FATURAR ({selectedIds.length})
                    </button>
                </div>
            )}

            <ResilientTimer onSaveSession={handlePreSave} autoStartData={handleAutoStartData} />
            
            <TimesheetList 
                entries={entries} 
                onDelete={handleDelete} 
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                onSelectAll={handleSelectAll}
            />

            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md border-t-4 border-blue-600 animate-fade-in-up">
                        <div className="p-6 border-b border-gray-100 bg-gray-50">
                            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2"><i className="fas fa-clock text-blue-600"></i> Registrar Sessão</h3>
                            <p className="text-xs text-gray-500 mt-1">Tempo: <strong className="text-brand-blue">{formatTime(tempSeconds)}</strong></p>
                        </div>
                        <div className="p-6 space-y-4">
                            <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Descrição</label><textarea className="w-full p-2 border rounded text-sm h-24 resize-none" value={form.description} onChange={e => setForm({...form, description: e.target.value})} autoFocus /></div>
                        </div>
                        <div className="p-4 bg-gray-50 flex justify-end gap-3 border-t border-gray-100"><button onClick={() => setShowModal(false)} className="px-4 py-2 text-xs font-bold text-gray-700 bg-white border border-gray-300 shadow-sm hover:bg-gray-50 hover:shadow-md rounded-lg transition-transform active:scale-95" type="button">DESCARTAR</button><button onClick={handleConfirmSave} className="px-4 py-2 text-xs font-bold text-white bg-[#20243B] border border-[#20243B] shadow-md hover:bg-[#1a1e30] hover:shadow-lg rounded-lg transition-transform active:scale-95" type="button">CONFIRMAR</button></div>
                    </div>
                </div>
            )}

            {showBillingModal && (
                <BillingModal 
                    selectedEntries={entries.filter(e => selectedIds.includes(e.id))}
                    onClose={() => setShowBillingModal(false)}
                    onConfirm={handleBillingConfirm}
                />
            )}
        </div>
    );
};